import { Link, useSearchParams } from "react-router";
import {
  Activity,
  Building2,
  CircleDollarSign,
  ExternalLink,
  Handshake,
  Inbox,
  TrendingUp,
  Wallet,
} from "lucide-react";
import { formatCurrencyEUR, formatRelative } from "@/lib/utils";
import { Skeleton } from "@/components/ui/skeleton";
import { MetricCard } from "@/components/shared/metric-card";
import { EmptyState } from "@/components/shared/empty-state";
import { DealDrawer } from "./deals/deal-drawer";
import {
  useAdminDashboard,
  type AdminDashboardDeal,
} from "./use-admin-dashboard";

// Phase 7 sub-bucket 9 — admin landing page. Ports
// /app/admin/dashboard/page.tsx: inlined header (analytics-page convention,
// since the SPA has no shared PageHeader), 5 metric cards, two recent-deal
// panels, partner leaderboard. One aggregate GET (useAdminDashboard)
// replaces the legacy's 8 parallel server-component Supabase reads.
//
// TierBadge / EmptyState are inlined as local components — the full TierBadge
// port lands with the admin partner-detail page in a later sub-bucket.
export function AdminDashboardPage() {
  const query = useAdminDashboard();
  const data = query.data ?? null;
  const monthName = new Date().toLocaleString("en-GB", { month: "long" });

  // Reuse the kanban drawer's `?deal=<hubspotDealId>` contract so a recent-deal
  // row opens the full detail drawer in place, here on the dashboard.
  const [searchParams, setSearchParams] = useSearchParams();
  const selectedDealId = searchParams.get("deal");
  function selectDeal(hubspotDealId: string | null) {
    const next = new URLSearchParams(searchParams);
    if (hubspotDealId) {
      next.set("deal", hubspotDealId);
    } else {
      next.delete("deal");
    }
    setSearchParams(next, { replace: true });
  }

  return (
    <div>
      <header className="mb-7 flex items-start justify-between gap-4">
        <div>
          <p className="text-xs font-medium uppercase tracking-wider text-muted-foreground">
            Admin
          </p>
          <h1 className="mt-1 font-display text-3xl font-semibold tracking-tight">
            Dashboard
          </h1>
          <p className="mt-1 max-w-2xl text-sm text-muted-foreground">
            Overview of partner activity, deal flow, and revenue.
          </p>
        </div>
        <Link
          to="/admin/deals"
          className="text-xs font-medium text-accent underline-offset-4 hover:underline"
        >
          Open pipeline →
        </Link>
      </header>

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
        {query.isLoading || !data ? (
          <>
            <Skeleton className="h-28 rounded-xl" />
            <Skeleton className="h-28 rounded-xl" />
            <Skeleton className="h-28 rounded-xl" />
            <Skeleton className="h-28 rounded-xl" />
            <Skeleton className="h-28 rounded-xl" />
          </>
        ) : (
          <>
            <MetricCard
              label="Active partners"
              value={String(data.metrics.activePartners)}
              icon={Building2}
              hint="Status = active"
            />
            <MetricCard
              label="Deals this month"
              value={String(data.metrics.hubspotDealsThisMonth)}
              icon={Handshake}
              hint={monthName}
            />
            <MetricCard
              label="Pipeline value"
              value={formatCurrencyEUR(data.metrics.openPipelineValueEur)}
              icon={TrendingUp}
              hint={`${data.metrics.openPipelineDealCount} live deal${
                data.metrics.openPipelineDealCount === 1 ? "" : "s"
              }`}
            />
            <MetricCard
              label="ARR — won"
              value={formatCurrencyEUR(data.metrics.closedWonArrEur)}
              icon={CircleDollarSign}
              hint="All-time closed-won"
            />
            <Link to="/admin/mdf" className="block">
              <MetricCard
                label="Open MDF funds"
                value={formatCurrencyEUR(data.metrics.openMdfTotalEur)}
                icon={Wallet}
                hint={`${data.metrics.openMdfFundsCount} open fund${
                  data.metrics.openMdfFundsCount === 1 ? "" : "s"
                }`}
              />
            </Link>
          </>
        )}
      </div>

      <div className="mt-8 grid gap-6 lg:grid-cols-3">
        <section className="rounded-xl border bg-card p-5 lg:col-span-2">
          <header className="mb-4 flex items-baseline justify-between">
            <div>
              <h2 className="font-display text-base font-semibold tracking-tight">
                Recent partner-sourced deals
              </h2>
              <p className="mt-0.5 text-xs text-muted-foreground">
                Last 10 deals partners brought to Bookboost. Bookboost-sent
                intros are tracked separately below.
              </p>
            </div>
            <Link
              to="/admin/deals"
              className="text-xs font-medium text-muted-foreground hover:text-foreground"
            >
              View all →
            </Link>
          </header>

          {query.isLoading || !data ? (
            <DealListSkeleton />
          ) : data.recentInboundDeals.length > 0 ? (
            <ul className="divide-y divide-border/70">
              {data.recentInboundDeals.map((d) => (
                <DealRow
                  key={d.hubspotDealId}
                  deal={d}
                  onSelect={() => selectDeal(d.hubspotDealId)}
                />
              ))}
            </ul>
          ) : (
            <EmptyState
              icon={Inbox}
              title="No partner-sourced deals yet"
              description="Once a partner refers a deal in HubSpot it'll appear here."
            />
          )}
        </section>

        <section className="rounded-xl border bg-card p-5">
          <header className="mb-4">
            <h2 className="font-display text-base font-semibold tracking-tight">
              Top partners by ARR
            </h2>
            <p className="mt-0.5 text-xs text-muted-foreground">
              Attributed closed-won deal value, all-time. Counts
              partner-sourced deals only.
            </p>
          </header>

          {query.isLoading || !data ? (
            <ul className="space-y-3">
              <Skeleton className="h-8 rounded" />
              <Skeleton className="h-8 rounded" />
              <Skeleton className="h-8 rounded" />
              <Skeleton className="h-8 rounded" />
              <Skeleton className="h-8 rounded" />
            </ul>
          ) : data.leaderboard.length > 0 ? (
            <ul className="space-y-3">
              {data.leaderboard.map((row, idx) => (
                <li key={row.partnerId} className="flex items-center gap-3">
                  <span className="w-5 text-xs font-medium tabular-nums text-muted-foreground">
                    {idx + 1}
                  </span>
                  <div className="min-w-0 flex-1">
                    <Link
                      to={`/admin/partners/${row.partnerId}`}
                      className="block truncate text-sm font-medium hover:underline"
                    >
                      {row.partnerName}
                    </Link>
                    <p className="text-[11px] text-muted-foreground">
                      {row.winCount} closed · {formatCurrencyEUR(row.arrEur)}
                    </p>
                  </div>
                  {row.partnerTier && <TierChip tier={row.partnerTier} />}
                </li>
              ))}
            </ul>
          ) : (
            <EmptyState
              icon={Activity}
              title="No closed-won deals yet"
              description="Win some deals to populate this leaderboard."
              className="px-4 py-8"
            />
          )}
        </section>

        <section className="rounded-xl border bg-card p-5 lg:col-span-3">
          <header className="mb-4">
            <h2 className="font-display text-base font-semibold tracking-tight">
              Recent Bookboost intros
            </h2>
            <p className="mt-0.5 text-xs text-muted-foreground">
              Last 10 deals Bookboost referred to a partner or co-influenced
              toward them. Excluded from the leaderboard above.
            </p>
          </header>

          {query.isLoading || !data ? (
            <DealListSkeleton />
          ) : data.recentOutboundDeals.length > 0 ? (
            <ul className="divide-y divide-border/70">
              {data.recentOutboundDeals.map((d) => (
                <DealRow
                  key={d.hubspotDealId}
                  deal={d}
                  onSelect={() => selectDeal(d.hubspotDealId)}
                />
              ))}
            </ul>
          ) : (
            <EmptyState
              icon={Inbox}
              title="No outbound intros yet"
              description="When a Bookboost owner refers a prospect to a partner in HubSpot it'll appear here."
            />
          )}
        </section>
      </div>

      <DealDrawer
        hubspotDealId={selectedDealId}
        onClose={() => selectDeal(null)}
      />
    </div>
  );
}

function DealRow({
  deal,
  onSelect,
}: {
  deal: AdminDashboardDeal;
  onSelect: () => void;
}) {
  const stageLabel = deal.dealStageLabel ?? deal.dealStageId ?? "—";
  return (
    <li className="flex items-center gap-2 py-3">
      <button
        type="button"
        onClick={onSelect}
        className="group flex min-w-0 flex-1 items-center gap-4 text-left"
      >
        <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-md bg-muted">
          <Handshake className="h-3.5 w-3.5 text-muted-foreground" />
        </div>
        <div className="min-w-0 flex-1">
          <p className="truncate text-sm font-medium group-hover:underline">
            {deal.dealName ?? "(unnamed deal)"}
          </p>
          <p className="mt-0.5 truncate text-xs text-muted-foreground">
            {deal.partnerName ?? "—"}
          </p>
        </div>
        <div className="hidden text-right text-xs text-muted-foreground sm:block">
          {formatCurrencyEUR(deal.dealAmountEur)}
        </div>
        <span className="inline-flex items-center rounded-full bg-muted px-2 py-0.5 text-[10px] font-medium uppercase tracking-wider text-muted-foreground">
          {stageLabel}
        </span>
        <div className="hidden w-24 text-right text-xs text-muted-foreground md:block">
          {formatRelative(deal.dealCreatedAt)}
        </div>
      </button>
      <a
        href={deal.hubspotUrl}
        target="_blank"
        rel="noreferrer noopener"
        onClick={(e) => e.stopPropagation()}
        className="shrink-0 rounded-md p-1.5 text-muted-foreground hover:bg-muted hover:text-foreground"
        title="Open in HubSpot"
        aria-label="Open in HubSpot"
      >
        <ExternalLink className="h-3.5 w-3.5" />
      </a>
    </li>
  );
}

function DealListSkeleton() {
  return (
    <ul className="divide-y divide-border/70">
      {[0, 1, 2, 3, 4].map((i) => (
        <li key={i} className="flex items-center gap-4 py-3">
          <Skeleton className="h-8 w-8 rounded-md" />
          <div className="min-w-0 flex-1 space-y-1.5">
            <Skeleton className="h-3.5 w-1/2 rounded" />
            <Skeleton className="h-3 w-1/3 rounded" />
          </div>
          <Skeleton className="hidden h-3 w-16 rounded sm:block" />
          <Skeleton className="h-4 w-14 rounded-full" />
          <Skeleton className="hidden h-3 w-16 rounded md:block" />
        </li>
      ))}
    </ul>
  );
}

function TierChip({ tier }: { tier: string }) {
  return (
    <span className="inline-flex items-center rounded-full border border-border/60 bg-muted/40 px-2 py-0.5 text-[10px] font-medium uppercase tracking-wider text-muted-foreground">
      {tier}
    </span>
  );
}
